import { useEffect, useState } from "react";
import { ALLERGENS, DIET_TYPES } from "../../../../../constants";
import { DietaryRequirement } from "../../../../../types/bookings";
import { EDSButton } from "../../../../eds/EDSButton";
import { Checkbox } from "../../../../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../ui/dialog";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";

type DietaryRequirementsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dietaryRequirement?: DietaryRequirement;
  onSave: (requirement: DietaryRequirement) => void;
};

export const DietaryRequirementsModal = ({
  open,
  onOpenChange,
  dietaryRequirement,
  onSave
}: DietaryRequirementsModalProps) => {
  const [name, setName] = useState("");
  const [selectedDietTypes, setSelectedDietTypes] = useState<string[]>([]);
  const [selectedAllergens, setSelectedAllergens] = useState<string[]>([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (dietaryRequirement) {
      setName(dietaryRequirement.name ?? "");
      setSelectedDietTypes(
        Array.isArray(dietaryRequirement.diet_types)
          ? dietaryRequirement.diet_types
          : []
      );
      setSelectedAllergens(
        Array.isArray(dietaryRequirement.allergies)
          ? dietaryRequirement.allergies
          : []
      );
      setNotes(dietaryRequirement.notes ?? "");
    } else {
      setName("");
      setSelectedDietTypes([]);
      setSelectedAllergens([]);
      setNotes("");
    }
  }, [dietaryRequirement, open]);

  const toggleDietType = (dietType: string) => {
    setSelectedDietTypes((prev) =>
      prev.includes(dietType)
        ? prev.filter((t) => t !== dietType)
        : [...prev, dietType]
    );
  };

  const toggleAllergen = (allergen: string) => {
    setSelectedAllergens((prev) =>
      prev.includes(allergen)
        ? prev.filter((a) => a !== allergen)
        : [...prev, allergen]
    );
  };

  const handleSave = () => {
    onSave({
      name: name.trim(),
      diet_types: Array.isArray(selectedDietTypes) ? selectedDietTypes : [],
      allergies: Array.isArray(selectedAllergens) ? selectedAllergens : [],
      notes: notes.trim() || undefined
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] lg:max-w-[1000px] max-h-[90vh] min-h-[400px] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Dietary Requirements</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4">
          <div className="flex flex-col gap-3">
            <label
              htmlFor="dietary-customer-name"
              className="font-medium text-sm"
            >
              Customer Name
            </label>
            <Input
              id="dietary-customer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter customer name"
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-semibold text-[15px] m-0">Diet Types</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {DIET_TYPES.map((dietType) => (
                <label
                  key={dietType}
                  className="flex items-center gap-2 cursor-pointer min-h-12 md:min-h-11 p-1 hover:bg-muted/50 rounded"
                >
                  <Checkbox
                    checked={selectedDietTypes.includes(dietType)}
                    onCheckedChange={() => toggleDietType(dietType)}
                    className="h-6 w-6"
                  />
                  <span className="text-sm select-none">{dietType}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-semibold text-[15px] m-0">Allergens</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {ALLERGENS.map((allergen) => (
                <label
                  key={allergen}
                  className="flex items-center gap-2 cursor-pointer min-h-12 md:min-h-11 p-1 hover:bg-muted/50 rounded"
                >
                  <Checkbox
                    checked={selectedAllergens.includes(allergen)}
                    onCheckedChange={() => toggleAllergen(allergen)}
                    className="h-6 w-6"
                  />
                  <span className="text-sm select-none">{allergen}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <label htmlFor="dietary-notes" className="font-medium text-sm">
              Additional Notes
            </label>
            <Textarea
              id="dietary-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional dietary information"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <EDSButton variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </EDSButton>
          <EDSButton variant="default" onClick={handleSave}>
            Done
          </EDSButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DietaryRequirementsModal;
